import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  getCreditBalance,
  getMarketplaceCatalog,
  getOwnedItems,
  purchaseItem,
  type CreditBalance,
  type OwnedItem,
  type CreditPack,
} from '@/lib/coreApi'
import { formatCreditsLabel, formatUsd, creditsToUsd } from '@/utils/credits'
import MarketplaceProductCard from './ProductCard'

// ---------- types ----------

interface CatalogItem {
  id: string
  sku?: string
  title: string
  description?: string | null
  previewUrl?: string | null
  priceCredits?: number | null
  priceCents?: number | null
  kind?: string
  meta?: Record<string, unknown>
  qtyLimitPerUser?: number | null
}

type Tab = 'shop' | 'ai-tools' | 'inventory'

const SHOP_KINDS = ['credit_pack', 'special_offer', 'badge', 'avatar', 'theme', 'border', 'digital_good'] as const
const SHOP_LABELS: Record<string, string> = {
  credit_pack: 'Credit Bundles',
  special_offer: 'Special Offers',
  badge: 'Badges',
  avatar: 'Avatars',
  theme: 'Themes',
  border: 'Borders',
  digital_good: 'Digital Goods',
}

// ---------- props ----------

export interface PortalMarketplaceProps {
  role: string
  title: string
  subtitle?: string
}

// ---------- component ----------

export default function PortalMarketplace({ role, title, subtitle }: PortalMarketplaceProps) {
  const [tab, setTab] = useState<Tab>('shop')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState<string | null>(null)

  const [balance, setBalance] = useState<CreditBalance>({ balance_credits: 0, balance_cents: 0, balance_usd: '0.00' })
  const [sections, setSections] = useState<Record<string, CatalogItem[]>>({})
  const [packs, setPacks] = useState<CreditPack[]>([])
  const [ownedIds, setOwnedIds] = useState<Set<string>>(new Set())
  const [ownedItems, setOwnedItems] = useState<OwnedItem[]>([])

  // ---- data loading ----

  const loadAll = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [catalogResult, ownedResult, balanceResult] = await Promise.allSettled([
        getMarketplaceCatalog(role),
        getOwnedItems(),
        getCreditBalance(),
      ])

      if (catalogResult.status === 'rejected') {
        throw catalogResult.reason
      }

      const catalogRes = catalogResult.value
      const ownedRes = ownedResult.status === 'fulfilled' ? ownedResult.value : null
      const balanceRes = balanceResult.status === 'fulfilled' ? balanceResult.value : null

      setSections(catalogRes.data.sections as unknown as Record<string, CatalogItem[]>)
      setPacks(catalogRes.data.packs || [])

      const owned = Array.isArray(ownedRes?.data) ? ownedRes.data : []
      setOwnedItems(owned)
      setOwnedIds(new Set(owned.map((o) => o.item_id)))

      setBalance(balanceRes?.data ?? { balance_credits: 0, balance_cents: 0, balance_usd: '0.00' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load marketplace')
    } finally {
      setLoading(false)
    }
  }, [role])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  // ---- purchase ----

  const buy = async (itemId: string, itemName: string) => {
    setBusy(itemId)
    setError('')
    setMessage('')
    try {
      const result = await purchaseItem({ item_id: itemId })
      setMessage(`Unlocked: ${itemName}`)
      setBalance((prev) => ({
        ...prev,
        balance_credits: result.data.new_balance,
      }))
      // Refresh owned items
      const ownedRes = await getOwnedItems()
      const owned = Array.isArray(ownedRes.data) ? ownedRes.data : []
      setOwnedItems(owned)
      setOwnedIds(new Set(owned.map((o) => o.item_id)))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Purchase failed')
    } finally {
      setBusy(null)
    }
  }

  // ---- derived data ----

  const shopSections = useMemo(() => {
    const result: { kind: string; label: string; items: CatalogItem[] }[] = []
    for (const kind of SHOP_KINDS) {
      const key = kind === 'credit_pack' ? 'credit_packs' : `${kind}s`
      const items = sections[key] || sections[kind] || []
      if (items.length > 0) {
        result.push({ kind, label: SHOP_LABELS[kind] || kind, items })
      }
    }
    return result
  }, [sections])

  const aiTools = useMemo(() => {
    return sections.ai_tools || []
  }, [sections])

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'shop', label: 'Shop', count: shopSections.reduce((sum, s) => sum + s.items.length, 0) },
    { id: 'ai-tools', label: 'AI Tools', count: aiTools.length },
    { id: 'inventory', label: 'My Items', count: ownedItems.length },
  ]

  // ---- render ----

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold text-cocoa">{title}</h1>
        {subtitle ? <p className="text-sm text-cocoa/70">{subtitle}</p> : (
          <p className="text-sm text-cocoa/70">Tower Credits are platform credits and cannot be redeemed off-platform.</p>
        )}
      </header>

      {/* Balance card */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white/80 p-4 shadow-soft">
        <div className="text-sm text-cocoa/70">Tower balance</div>
        <div className="text-2xl font-semibold text-cocoa">
          {loading ? '—' : (
            <>
              {formatCreditsLabel(balance.balance_credits)}
              <span className="block text-sm font-normal text-cocoa/60">
                ≈ {formatUsd(creditsToUsd(balance.balance_credits))}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Feedback messages */}
      {message ? (
        <div className="rounded-xl border border-mint/40 bg-mint/10 px-4 py-3 text-sm text-cocoa/80">{message}</div>
      ) : null}
      {error ? (
        <div className="rounded-xl border border-brick/40 bg-brick/10 px-4 py-3 text-sm text-brick">{error}</div>
      ) : null}

      {/* Tab bar */}
      <div className="flex items-center gap-1 rounded-full bg-shell/50 p-1">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
              tab === t.id
                ? 'bg-cocoa text-white shadow-sm'
                : 'text-cocoa/70 hover:bg-white/60 hover:text-cocoa'
            }`}
          >
            {t.label}
            {t.count != null && t.count > 0 ? (
              <span className="ml-1.5 text-xs font-normal opacity-70">{t.count}</span>
            ) : null}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {loading ? (
        <p className="text-sm text-cocoa/70">Loading marketplace…</p>
      ) : (
        <>
          {tab === 'shop' && (
            <ShopTab
              sections={shopSections}
              packs={packs}
              ownedIds={ownedIds}
              busy={busy}
              onBuy={buy}
            />
          )}
          {tab === 'ai-tools' && (
            <AiToolsTab
              items={aiTools}
              ownedIds={ownedIds}
              busy={busy}
              onBuy={buy}
            />
          )}
          {tab === 'inventory' && (
            <InventoryTab items={ownedItems} />
          )}
        </>
      )}
    </div>
  )
}

// ---------- Shop tab ----------

function ShopTab({
  sections,
  packs,
  ownedIds,
  busy,
  onBuy,
}: {
  sections: { kind: string; label: string; items: CatalogItem[] }[]
  packs: CreditPack[]
  ownedIds: Set<string>
  busy: string | null
  onBuy: (itemId: string, name: string) => void
}) {
  const hasContent = sections.length > 0 || packs.length > 0

  if (!hasContent) {
    return (
      <div className="rounded-3xl border border-dashed border-sand bg-white/70 p-8 text-center text-sm text-cocoa/70">
        No shop items are available right now. Check back soon.
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Credit packs from the separate packs array (if not already in sections) */}
      {packs.length > 0 && !sections.some((s) => s.kind === 'credit_pack') ? (
        <section className="space-y-4">
          <h2 className="text-lg font-semibold text-cocoa">Credit Bundles</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {packs.map((pack) => (
              <MarketplaceProductCard
                key={pack.id}
                title={pack.name}
                description={`Get ${Math.round((pack.credit_value_cents / 100) * 100)} credits`}
                price={`$${(pack.usd_price / 100).toFixed(2)}`}
                actionLabel="Buy"
                imageUrl={null}
                tags={['credit pack']}
              />
            ))}
          </div>
        </section>
      ) : null}

      {sections.map((section) => (
        <section key={section.kind} className="space-y-4">
          <h2 className="text-lg font-semibold text-cocoa">{section.label}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {section.items.map((item) => {
              const owned = ownedIds.has(item.id)
              const price = item.priceCredits
                ? `${formatCreditsLabel(item.priceCredits)} (≈ ${formatUsd(creditsToUsd(item.priceCredits))})`
                : 'Free'
              return (
                <MarketplaceProductCard
                  key={item.id}
                  title={item.title}
                  description={item.description}
                  price={price}
                  actionLabel={owned ? 'Owned' : 'Buy'}
                  disabled={owned}
                  loading={busy === item.id}
                  onAction={owned ? undefined : () => onBuy(item.id, item.title)}
                  imageUrl={item.previewUrl}
                  tags={item.kind ? [item.kind] : undefined}
                />
              )
            })}
          </div>
        </section>
      ))}
    </div>
  )
}

// ---------- AI Tools tab ----------

function AiToolsTab({
  items,
  ownedIds,
  busy,
  onBuy,
}: {
  items: CatalogItem[]
  ownedIds: Set<string>
  busy: string | null
  onBuy: (itemId: string, name: string) => void
}) {
  if (items.length === 0) {
    return (
      <div className="rounded-3xl border border-dashed border-sand bg-white/70 p-8 text-center text-sm text-cocoa/70">
        No AI tools are available for your role right now.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-cocoa/70">
        AI tools cost credits to activate and may have per-use costs.
      </p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => {
          const owned = ownedIds.has(item.id)
          const tcCost = item.meta?.tc_cost as number | undefined
          const price = item.priceCredits
            ? `${formatCreditsLabel(item.priceCredits)} to activate`
            : 'Free'
          const footer = tcCost ? `${tcCost} TC per use` : undefined

          return (
            <MarketplaceProductCard
              key={item.id}
              title={item.title}
              description={item.description}
              price={price}
              actionLabel={owned ? 'Activated' : 'Activate'}
              disabled={owned}
              loading={busy === item.id}
              onAction={owned ? undefined : () => onBuy(item.id, item.title)}
              imageUrl={item.previewUrl}
              tags={['ai tool']}
              footer={footer ? <span>{footer}</span> : undefined}
            />
          )
        })}
      </div>
    </div>
  )
}

// ---------- Inventory tab ----------

function InventoryTab({ items }: { items: OwnedItem[] }) {
  if (items.length === 0) {
    return (
      <div className="rounded-3xl border border-dashed border-sand bg-white/70 p-8 text-center text-sm text-cocoa/70">
        You don't own any items yet. Browse the Shop or AI Tools to get started.
      </div>
    )
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((entry) => {
        const item = entry.item
        if (!item) return null
        const acquiredDate = entry.acquired_at
          ? new Date(entry.acquired_at).toLocaleDateString()
          : null

        return (
          <div
            key={`${entry.item_id}-${entry.acquired_at}`}
            className="flex h-full flex-col justify-between rounded-3xl bg-white/90 p-5 shadow-soft ring-1 ring-sand/40"
          >
            <div className="space-y-3">
              {item.preview_url ? (
                <img
                  src={item.preview_url}
                  alt=""
                  className="h-16 w-16 rounded-2xl border border-shell/70 object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-shell/40 bg-sand/30 text-xs font-semibold uppercase text-cocoa/50">
                  {item.kind || 'item'}
                </div>
              )}
              <div>
                <h3 className="text-base font-semibold text-cocoa">{item.title}</h3>
                {item.description ? (
                  <p className="text-sm text-cocoa/70">{item.description}</p>
                ) : null}
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between gap-2 text-xs text-cocoa/60">
              <span className="rounded-full bg-mint/20 px-2 py-0.5 font-semibold uppercase tracking-wide text-emerald-700">
                Owned
              </span>
              {acquiredDate ? <span>Acquired {acquiredDate}</span> : null}
            </div>
          </div>
        )
      })}
    </div>
  )
}
